import { Object3D } from "three";
import { GLTFExporter } from "three/examples/jsm/exporters/GLTFExporter";

import Xbot from "@/assets/poser/Xbot.glb";
import { UploadedFileFragment } from "@/features/drive/queries.graphql";

import {
  PoseData,
  PosePresetData,
  PosePresetName,
  PoseViewData,
  ViewPresetData,
  ViewPresetName,
} from "./types";

export const exportGLTF = (object: Object3D) => {
  const exporter = new GLTFExporter();
  return new Promise<ArrayBuffer>((resolve, reject) => {
    exporter.parse(
      object,
      (gltf) => resolve(gltf as ArrayBuffer),
      (error) => reject(error),
      { binary: true },
    );
  });
};

// views
export const cameraFov = 45;

const frontCameraPosition = (
  height: number,
  distance: number,
): [number, number, number] => {
  return [0, height, distance];
};

const frontShot = (height: number, distance: number): PoseViewData => {
  return {
    camera: { position: frontCameraPosition(height, distance) },
    orbitControl: { target: [0, height, 0] },
  };
};

export const viewPresets: { [key in ViewPresetName]: ViewPresetData } = {
  fullShot: {
    label: "Full shot",
    data: frontShot(0.89, 2.5),
  },
  mediumFullShot: {
    label: "Medium full shot",
    data: frontShot(1.2, 1.67),
  },
  mediumShot: {
    label: "Medium shot",
    data: frontShot(1.4, 1.1),
  },
  highAngle: {
    label: "High angle",
    data: {
      camera: { position: [0.46, 2.11, 0.77] },
      orbitControl: { target: [0, 1.41, 0] },
    },
  },
  lowAngle: {
    label: "Low angle",
    data: {
      camera: { position: [1.3, 0.33, 1.63] },
      orbitControl: { target: [0, 0.89, 0] },
    },
  },
};

// poses
export const posePresets: { [key in PosePresetName]: PosePresetData } = {
  tPose: {
    label: "T-Pose",
    data: { modelUrl: window.location.origin + Xbot },
  },
};

// file names
export const glbAttachmentName = "model.glb";

export const removeExtension = (fileName: string): string => {
  return fileName.replace(/\.[^/.]+$/, "");
};

// files
export const poseDataFromFile = (
  file: UploadedFileFragment,
  prev: PoseData,
): PoseData => {
  return {
    ...prev,
    selectedFileId: file.id,
    fileId: file.id,
    name: removeExtension(file.name),
    modelUrl: file.attachmentUrls?.[glbAttachmentName] || prev.modelUrl,
    modelLoadVersion: prev.modelLoadVersion + 1,
    camera: file.metadata?.poseData?.camera || prev.camera,
    orbitControl: file.metadata?.poseData?.orbitControl || prev.orbitControl,
    imageUrl: file.url,
  };
};

export const incrementCacheVersionsFromFile = (
  file: UploadedFileFragment,
  prev: PoseData,
): PoseData["urlCacheVersions"] => {
  const cacheVersions = { ...prev.urlCacheVersions };
  if (file.url) {
    cacheVersions[file.url] = (cacheVersions[file.url] || 0) + 1;
  }
  if (file.attachmentUrls?.[glbAttachmentName]) {
    cacheVersions[file.attachmentUrls[glbAttachmentName]] =
      (cacheVersions[file.attachmentUrls[glbAttachmentName]] || 0) + 1;
  }
  return cacheVersions;
};

export const urlWithCacheVersion = (url: string, data: PoseData): string => {
  const cacheVersion = data.urlCacheVersions[url] || 0;
  const urlObject = new URL(url);
  const params = new URLSearchParams(urlObject.search);
  params.append("_cache", String(cacheVersion));
  return `${urlObject}?${params}`;
};
